import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import type { Me } from '../api/types';
import { resolveEntry } from '../api/types';
import { Frame } from './Frame';
import { BookingProvider } from '../client/BookingContext';
import { OnboardingProvider } from '../onboarding/OnboardingContext';
import { Welcome } from '../onboarding/screens/Welcome';
import { ChildStep } from '../onboarding/screens/ChildStep';
import { DirectionStep } from '../onboarding/screens/DirectionStep';
import { Result } from '../onboarding/screens/Result';
import { Sent } from '../onboarding/screens/Sent';
import { Today } from '../staff/screens/Today';
import { Calendar as StaffCalendar } from '../staff/screens/Calendar';
import { Kids } from '../staff/screens/Kids';
import { Hours } from '../staff/screens/Hours';
import { Off } from '../staff/screens/Off';
import { SeriesEditor } from '../staff/screens/SeriesEditor';
import { Center } from '../admin/screens/Center';
import { Staff, StaffAdd } from '../admin/screens/Staff';
import { StaffSchedule, StaffServices } from '../admin/screens/StaffDetail';
import { ServiceAdd, ServiceCard, Services } from '../admin/screens/Services';
import { ClientAdd, Clients } from '../admin/screens/Clients';
import { Org } from '../admin/screens/Org';
import { OrgProfile } from '../admin/screens/OrgProfile';
import { OrgAddress } from '../admin/screens/OrgAddress';
import { OrgAddresses } from '../admin/screens/OrgAddresses';
import { OrgSchedule } from '../admin/screens/OrgSchedule';
import { OrgNotifications } from '../admin/screens/OrgNotifications';
import { OrgSubscription } from '../admin/screens/OrgSubscription';
import { OrgHours } from '../admin/screens/OrgHours';
import { OrgCategories } from '../admin/screens/OrgCategories';
import { OrgAdmins } from '../admin/screens/OrgAdmins';
import { OrgAudit } from '../admin/screens/OrgAudit';
import { TenantNew, Tenants } from '../owner/screens/Tenants';
import { TenantCard } from '../owner/screens/TenantCard';
import { Platform } from '../owner/screens/Platform';
import { Home } from '../client/screens/Home';
import { Pick } from '../client/screens/Pick';
import { Service } from '../client/screens/Service';
import { Calendar } from '../client/screens/Calendar';
import { Form } from '../client/screens/Form';
import { Confirm } from '../client/screens/Confirm';
import { Done } from '../client/screens/Done';
import { rootPathFor } from './nav';
import { useMeContext } from './MeContext';

/**
 * Маршруты собираются под роль, которую вернул сервер: клиенту неоткуда попасть
 * в админку не потому, что маршрут спрятан, а потому что его нет. Спрятанная
 * кнопка — не защита, поэтому сервер всё равно проверяет права на каждом запросе.
 *
 * Заголовки экранов — из `CONF` прототипа.
 */

const client: RouteObject[] = [
  { index: true, element: <ClientEntry /> },
  { path: 'pick', element: <Pick /> },
  { path: 'service', element: <Service /> },
  { path: 'calendar', element: <Calendar /> },
  { path: 'form', element: <Form /> },
  { path: 'confirm', element: <Confirm /> },
  { path: 'done', element: <Done /> },
];

const onboarding: RouteObject[] = [
  { index: true, element: <Welcome /> },
  { path: 'child', element: <ChildStep /> },
  { path: 'direction', element: <DirectionStep /> },
  { path: 'result', element: <Result /> },
  { path: 'sent', element: <Sent /> },
];

const staff: RouteObject[] = [
  { index: true, element: <Today /> },
  { path: 'calendar', element: <StaffCalendar /> },
  { path: 'kids', element: <Kids /> },
  { path: 'kids/:childId/series', element: <SeriesEditor /> },
  { path: 'hours', element: <Hours /> },
  { path: 'off', element: <Off /> },
];

const admin: RouteObject[] = [
  { index: true, element: <Center /> },
  { path: 'staff', element: <Staff /> },
  { path: 'staff/add', element: <StaffAdd /> },
  { path: 'staff/:id/services', element: <StaffServices /> },
  { path: 'staff/:id/schedule', element: <StaffSchedule /> },
  { path: 'services', element: <Services /> },
  { path: 'services/add', element: <ServiceAdd /> },
  { path: 'services/:id', element: <ServiceCard /> },
  { path: 'clients', element: <Clients /> },
  { path: 'clients/add', element: <ClientAdd /> },
  // Карточка организации разложена по разделам: на вкладке остаётся оглавление,
  // а профиль, адреса, расписание, уведомления и подписка живут своими экранами
  // со своими кнопками сохранения.
  { path: 'org', element: <Org /> },
  { path: 'org/profile', element: <OrgProfile /> },
  { path: 'org/addresses', element: <OrgAddresses /> },
  { path: 'org/address/:id', element: <OrgAddress /> },
  { path: 'org/schedule', element: <OrgSchedule /> },
  { path: 'org/notifications', element: <OrgNotifications /> },
  { path: 'org/subscription', element: <OrgSubscription /> },
  { path: 'org/hours', element: <OrgHours /> },
  { path: 'org/categories', element: <OrgCategories /> },
  { path: 'org/admins', element: <OrgAdmins /> },
  { path: 'org/audit', element: <OrgAudit /> },
  // Категории переехали в карточку организации; старая ссылка из «Услуг» жива.
  { path: 'services/dirs', element: <Navigate to="/admin/org/categories" replace /> },
];

const owner: RouteObject[] = [
  { index: true, element: <Tenants /> },
  { path: 'new', element: <TenantNew /> },
  { path: 'settings', element: <Platform /> },
  { path: ':id', element: <TenantCard /> },
];

/**
 * Группа маршрутов: набор экранов меняется реже, чем точка входа. Новичок,
 * заполнивший анкету, перестаёт быть новичком прямо посреди пути — и роутер
 * не должен пересобираться под ним, унося текущий экран.
 */
export type RouteGroup = 'client' | 'staff' | 'admin' | 'owner';

export function routeGroupOf(me: Me): RouteGroup {
  const entry = resolveEntry(me);
  if (entry === 'owner') return 'owner';
  if (entry === 'staff') return me.isAdmin ? 'admin' : 'staff';
  return 'client';
}

export function routerFor(me: Me) {
  const entry = routeGroupOf(me);
  const children: RouteObject[] = [];

  if (entry === 'client') {
    children.push(...client, { path: 'onboarding', children: onboarding });
  }
  if (entry === 'staff' || entry === 'admin') {
    // Рабочее место — только тем, у кого есть карточка специалиста. Администратор
    // без неё приёмов не ведёт, и `/staff` для него не экран, а тупик: сервер
    // ответит NO_SPECIALIST_CARD. Маршрута нет — значит и тупика нет.
    if (me.specialistId) children.push({ path: 'staff', children: staff });
    if (entry === 'admin') children.push({ path: 'admin', children: admin });
  }
  if (entry === 'owner') children.push({ path: 'owner', children: owner });

  const root = rootPathFor(me);
  // У клиента корень «/» уже занят: там ClientEntry сам решает, новичок это или нет.
  if (entry !== 'client') children.push({ index: true, element: <Navigate to={root} replace /> });
  children.push({ path: '*', element: <Navigate to={root} replace /> });

  const element = entry === 'client'
    ? <BookingProvider><OnboardingProvider><Frame /></OnboardingProvider></BookingProvider>
    : <Frame />;

  return createBrowserRouter([{ path: '/', element, children }]);
}

/**
 * Корень клиентских экранов. Новичка уводим в онбординг, но решаем это на живом
 * `/api/me`, а не при сборке маршрутов: пока человек заполняет анкету, он как раз
 * перестаёт быть новичком, и застывшее решение увело бы его по кругу.
 */
function ClientEntry() {
  const me = useMeContext();
  return me.needsOnboarding ? <Navigate to="/onboarding" replace /> : <Home />;
}
